import React, { useState, useEffect } from 'react';
import {
  setStudyGoal,
  getStudyGoal,
  getStreak,
  getSectionMetrics,
} from '../utils/analytics';
import { getDailyStudyPlan } from '../utils/spacedRepetition';
import { MBTA_SECTIONS, QUESTION_BANKS, SECTION_META } from '../data/index';
import '../styles/StudyPlanning.css';

export default function StudyPlanning({ onHome, onStartPractice, onGoPreTripInspection, onGoPreTripFlashcards, onGoPreTripQuiz, onGoPreTripStudyPlan }) {
  const [goal, setGoal] = useState(null);
  const [streak, setStreak] = useState(0);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [goalForm, setGoalForm] = useState({
    targetDate: '',
    daysPerWeek: 5,
    minutesPerDay: 45,
  });
  const [dailyPlan, setDailyPlan] = useState(null);

  useEffect(() => {
    const existingGoal = getStudyGoal();
    setGoal(existingGoal);
    setStreak(getStreak());

    // Generate daily plan
    const allQuestions = Object.values(QUESTION_BANKS).flat();
    const plan = getDailyStudyPlan(allQuestions, 20);
    setDailyPlan(plan);
  }, []);

  const handleSetGoal = () => {
    if (!goalForm.targetDate) {
      alert('Please select a target date');
      return;
    }

    const newGoal = {
      targetDate: goalForm.targetDate,
      daysPerWeek: goalForm.daysPerWeek,
      minutesPerDay: goalForm.minutesPerDay,
      createdAt: new Date().toISOString(),
    };

    setStudyGoal(newGoal);
    setGoal(newGoal);
    setShowGoalForm(false);
  };

  const getDaysRemaining = () => {
    if (!goal) return 0;
    const today = new Date();
    const target = new Date(goal.targetDate);
    const daysMs = target - today;
    return Math.max(0, Math.ceil(daysMs / (1000 * 60 * 60 * 24)));
  };

  const getProgressPercentage = () => {
    if (!goal) return 0;
    const createdDate = new Date(goal.createdAt);
    const targetDate = new Date(goal.targetDate);
    const today = new Date();

    const totalDays = (targetDate - createdDate) / (1000 * 60 * 60 * 24);
    const daysPassed = (today - createdDate) / (1000 * 60 * 60 * 24);

    return Math.min(100, Math.round((daysPassed / totalDays) * 100));
  };

  return (
    <div className="study-planning-container">
      {/* Header */}
      <div className="planning-header">
        <button className="btn-back" onClick={onHome}>← Back</button>
        <h1>📅 Study Planning</h1>
      </div>

      {/* Streak Card */}
      <div className="planning-card streak-card">
        <div className="streak-display">
          <span className="streak-emoji">🔥</span>
          <div className="streak-info">
            <h2>{streak} Day Streak!</h2>
            <p>Keep it up! You're building momentum.</p>
          </div>
        </div>
      </div>

      {/* Goal Card */}
      <div className="planning-card">
        <div className="card-header">
          <h2>🎯 Your Study Goal</h2>
          {goal && (
            <button
              className="btn-edit"
              onClick={() => setShowGoalForm(true)}
            >
              ✏️ Edit
            </button>
          )}
        </div>

        {goal ? (
          <div className="card-body">
            <div className="goal-info">
              <div className="goal-item">
                <span>Target Exam Date</span>
                <strong>{new Date(goal.targetDate).toLocaleDateString()}</strong>
              </div>
              <div className="goal-item">
                <span>Days Remaining</span>
                <strong style={{ color: getDaysRemaining() <= 7 ? '#dc2626' : '#1a56db' }}>
                  {getDaysRemaining()} days
                </strong>
              </div>
              <div className="goal-item">
                <span>Study Schedule</span>
                <strong>{goal.daysPerWeek} days/week · {goal.minutesPerDay} min/day</strong>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="goal-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
              <small>{getProgressPercentage()}% complete</small>
            </div>

            {/* Daily Target */}
            <div className="daily-target">
              <h4>📊 Daily Target</h4>
              <div className="target-stats">
                <div className="stat">
                  <span className="stat-icon">❓</span>
                  <span className="stat-label">Questions/Day</span>
                  <span className="stat-value">
                    {Math.ceil((QUESTION_BANKS['General Knowledge']?.length || 246) / (getDaysRemaining() || 1))}
                  </span>
                </div>
                <div className="stat">
                  <span className="stat-icon">⏱️</span>
                  <span className="stat-label">Time/Day</span>
                  <span className="stat-value">{goal.minutesPerDay} min</span>
                </div>
                <div className="stat">
                  <span className="stat-icon">📈</span>
                  <span className="stat-label">Sections/Week</span>
                  <span className="stat-value">{Math.ceil(3 / (7 / goal.daysPerWeek))}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="card-body empty">
            <p>No goal set yet. Create one to get started!</p>
            <button className="btn-primary" onClick={() => setShowGoalForm(true)}>
              Set Your Goal →
            </button>
          </div>
        )}
      </div>

      {/* Goal Form */}
      {showGoalForm && (
        <div className="planning-card form-card">
          <div className="card-header">
            <h3>Set Your Study Goal</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label>When do you want to take the exam?</label>
              <input
                type="date"
                value={goalForm.targetDate}
                onChange={(e) => setGoalForm({ ...goalForm, targetDate: e.target.value })}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Days per week to study</label>
                <select
                  value={goalForm.daysPerWeek}
                  onChange={(e) => setGoalForm({ ...goalForm, daysPerWeek: parseInt(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5, 6, 7].map(d => (
                    <option key={d} value={d}>{d} days/week</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Minutes per day</label>
                <select
                  value={goalForm.minutesPerDay}
                  onChange={(e) => setGoalForm({ ...goalForm, minutesPerDay: parseInt(e.target.value) })}
                >
                  {[15, 30, 45, 60, 90, 120].map(m => (
                    <option key={m} value={m}>{m} min/day</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn-primary" onClick={handleSetGoal}>
                Save Goal
              </button>
              <button className="btn-secondary" onClick={() => setShowGoalForm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Daily Study Plan - Pre-Trip Inspection Focus */}
      <div className="planning-card pretrip-daily-study">
        <div className="card-header pretrip-daily-header">
          <h2>✅ Today's Pre-Trip Study Plan</h2>
        </div>
        <div className="card-body">
          <div className="daily-study-summary">
            <div className="study-stat">
              <span className="stat-label">Micro-Sections to Study</span>
              <span className="stat-value">3-4</span>
            </div>
            <div className="study-stat">
              <span className="stat-label">Time Required</span>
              <span className="stat-value">45 min</span>
            </div>
            <div className="study-stat">
              <span className="stat-label">Quiz Questions</span>
              <span className="stat-value">10-15</span>
            </div>
            <div className="study-stat">
              <span className="stat-label">Items to Master</span>
              <span className="stat-value">12-18</span>
            </div>
          </div>

          <div className="daily-focus-box">
            <h4>📚 Today's Focus</h4>
            <p>Start with the Cabin section (6 subsections) using voice reading + micro-section guide. Master 3-4 micro-sections, then practice with flashcards.</p>
          </div>

          <button
            className="btn-primary full-width pretrip-study-btn"
            onClick={onGoPreTripInspection}
          >
            Start Pre-Trip Study Now →
          </button>
        </div>
      </div>

      {/* PRE-TRIP INSPECTION EXAM PREP */}
      <div className="planning-card pretrip-card">
        <div className="card-header pretrip-header">
          <div>
            <h2>🚌 Pre-Trip Inspection Exam Prep</h2>
            <p className="pretrip-subtitle">Master 57 items for CDL Class B</p>
          </div>
          <span className="exam-badge">EXAM NEXT FRIDAY</span>
        </div>
        <div className="card-body">
          {/* Progress Overview */}
          <div className="pretrip-overview">
            <div className="overview-stat">
              <div className="stat-display">
                <span className="stat-emoji">📋</span>
                <div className="stat-content">
                  <span className="stat-label">Total Items</span>
                  <span className="stat-value">57</span>
                </div>
              </div>
            </div>
            <div className="overview-stat">
              <div className="stat-display">
                <span className="stat-emoji">⚠️</span>
                <div className="stat-content">
                  <span className="stat-label">Critical Items</span>
                  <span className="stat-value">41</span>
                </div>
              </div>
            </div>
            <div className="overview-stat">
              <div className="stat-display">
                <span className="stat-emoji">📚</span>
                <div className="stat-content">
                  <span className="stat-label">Sections</span>
                  <span className="stat-value">9</span>
                </div>
              </div>
            </div>
            <div className="overview-stat">
              <div className="stat-display">
                <span className="stat-emoji">🎯</span>
                <div className="stat-content">
                  <span className="stat-label">Study Days Left</span>
                  <span className="stat-value">{getDaysRemaining()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Study Plan Tips */}
          <div className="pretrip-tips">
            <h4>📖 Recommended Study Approach</h4>
            <ul className="tips-list">
              <li>✅ <strong>Use Voice Reading:</strong> Listen to each section with the Pre-Trip app</li>
              <li>✅ <strong>Master Subsections:</strong> Study one small section at a time (8-15 min each)</li>
              <li>✅ <strong>Flashcard Practice:</strong> Test yourself on critical PSI values and procedures</li>
              <li>✅ <strong>Quiz Simulation:</strong> Take practice quizzes to simulate exam conditions</li>
              <li>✅ <strong>Exact Wording:</strong> Memorize exact phrases (Air Brakes, External Lights)</li>
            </ul>
          </div>

          {/* Critical Focus Areas */}
          <div className="pretrip-critical">
            <h4>⭐ Critical Focus Areas (Heavily Tested)</h4>
            <div className="critical-grid">
              <div className="critical-item">
                <strong>PSI Values:</strong> 100, 120-140, 55, 20-45, 85-100
              </div>
              <div className="critical-item">
                <strong>Leak Tests:</strong> 2 psi (Static) vs 3 psi (Applied)
              </div>
              <div className="critical-item">
                <strong>Emergency Exits:</strong> 9 total (2 roof, 2 doors, 5 windows)
              </div>
              <div className="critical-item">
                <strong>Light Counts:</strong> Front, Left, Rear, Right (specific numbers)
              </div>
              <div className="critical-item">
                <strong>Tire Specs:</strong> 4/32" tread depth, 120 psi pressure
              </div>
              <div className="critical-item">
                <strong>Lug Nuts:</strong> 10 per wheel (count them!)
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pretrip-actions">
            <button className="btn-primary pretrip-btn" onClick={onGoPreTripInspection}>
              🔊 Voice Reading & Study
            </button>
            <button className="btn-secondary pretrip-btn" onClick={onGoPreTripFlashcards}>
              📚 Practice Flashcards
            </button>
            <button className="btn-secondary pretrip-btn" onClick={onGoPreTripQuiz}>
              ✅ Take Quiz
            </button>
            <button className="btn-secondary pretrip-btn" onClick={onGoPreTripStudyPlan}>
              📖 View Study Plan
            </button>
          </div>

          {/* Daily Micro-Section Goal */}
          <div className="pretrip-daily-goal">
            <h4>📅 Daily Micro-Section Goal</h4>
            <p>Study <strong>3-4 micro-sections per day</strong> (8-15 min each)</p>
            <div className="micro-sections-row">
              <div className="micro-section-item">
                <strong>Mon:</strong> Cabin (6 subsections)
              </div>
              <div className="micro-section-item">
                <strong>Tue:</strong> Brakes + Air (Part 1)
              </div>
              <div className="micro-section-item">
                <strong>Wed:</strong> Air (Part 2) + Internal
              </div>
              <div className="micro-section-item">
                <strong>Thu:</strong> External Left + Power Steering
              </div>
              <div className="micro-section-item">
                <strong>Fri:</strong> Tires + Brakes + External Complete
              </div>
              <div className="micro-section-item">
                <strong>Sat:</strong> Full Simulation
              </div>
              <div className="micro-section-item">
                <strong>Sun:</strong> Rest & Final Review
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Start */}
      <div className="planning-card quick-start">
        <div className="card-header">
          <h3>🚀 Ready to Start?</h3>
        </div>
        <div className="card-body">
          <p>Begin with the Cabin section using voice reading + the micro-section study guide included in the app.</p>
          <button className="btn-primary full-width" onClick={onGoPreTripInspection}>
            Open Pre-Trip Inspection Now
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
